import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, GraduationCap, Users, MessagesSquare } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const SearchBar = ({ className = '', onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({
    courses: [],
    discussions: [],
    users: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Flatten results for keyboard navigation
  const flatResults = [
    ...results.courses.map(item => ({ ...item, type: 'course' })),
    ...results.discussions.map(item => ({ ...item, type: 'discussion' })),
    ...results.users.map(item => ({ ...item, type: 'user' }))
  ];

  // Search function
  const performSearch = async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults({ courses: [], discussions: [], users: [] });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Concurrent searches
      const [coursesResult, discussionsResult, profilesResult] = await Promise.all([
        // Search courses
        supabase
          .from('courses')
          .select('id, title, description, level')
          .ilike('title', `%${searchQuery}%`)
          .limit(3),

        // Search discussions
        supabase
          .from('discussions')
          .select('id, title, content')
          .ilike('title', `%${searchQuery}%`)
          .limit(3),

        // Search users/profiles
        supabase
          .from('profiles')
          .select('id, full_name')
          .ilike('full_name', `%${searchQuery}%`)
          .limit(3)
      ]);

      setResults({
        courses: coursesResult.data || [],
        discussions: discussionsResult.data || [],
        users: profilesResult.data || []
      });

    } catch (err) {
      setError('Failed to perform search');
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => performSearch(query), 300);
    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < flatResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClear();
    }
  };

  // Handle result selection
  const handleSelect = (item) => {
    if (onSelect) {
      onSelect(item);
    } else {
      // Default navigation based on type
      switch (item.type) {
        case 'course':
          window.location.href = `/course/${item.id}`;
          break;
        case 'discussion':
          window.location.href = `/community/discussion/${item.id}`;
          break;
        case 'user':
          window.location.href = `/profile/${item.id}`;
          break;
        default:
          break;
      }
    }
    handleClear();
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults({ courses: [], discussions: [], users: [] });
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  // Render result item
  const ResultItem = ({ item, index, type }) => {
    const isSelected = index === selectedIndex;
    const icons = {
      course: GraduationCap,
      discussion: MessagesSquare,
      user: Users
    };
    const Icon = icons[type];

    return (
      <button
        className={`w-full px-4 py-2 flex items-center gap-3 text-left ${
          isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
        }`}
        onClick={() => handleSelect(item)}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <Icon size={18} className="text-blue-900 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 truncate">
            {type === 'course' ? item.title :
             type === 'discussion' ? item.title :
             item.full_name}
          </div>
          {item.description && (
            <div className="text-sm text-gray-500 truncate">
              {item.description}
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 100)}
        onKeyDown={handleKeyDown}
        placeholder="Search courses, discussions, and people..."
        className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-900/20 focus:border-blue-900"
        aria-label="Search"
      />
      <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
      
      {isLoading && (
        <Loader2 className="absolute right-3 top-2.5 animate-spin text-gray-400" size={20} />
      )}

      {isFocused && (query.length >= 2 || error) && (
        <div
          ref={resultsRef}
          className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto"
        >
          {error ? (
            <div className="p-4 text-center text-red-600">
              {error}
            </div>
          ) : flatResults.length > 0 ? (
            <div className="py-2">
              {results.courses.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                    Courses
                  </div>
                  {results.courses.map((course, idx) => (
                    <ResultItem
                      key={course.id}
                      item={course}
                      type="course"
                      index={idx}
                    />
                  ))}
                </div>
              )}

              {results.discussions.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                    Discussions
                  </div>
                  {results.discussions.map((discussion, idx) => (
                    <ResultItem
                      key={discussion.id}
                      item={discussion}
                      type="discussion"
                      index={results.courses.length + idx}
                    />
                  ))}
                </div>
              )}

              {results.users.length > 0 && (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                    People
                  </div>
                  {results.users.map((user, idx) => (
                    <ResultItem
                      key={user.id}
                      item={user}
                      type="user"
                      index={results.courses.length + results.discussions.length + idx}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-gray-500">
              No results found
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchBar;